import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Button, ActionIcon, Group, Loader, Alert, Text, Title,
  Table, ScrollArea, Paper, TextInput, Select, Radio,
  Tooltip, useComputedColorScheme, Tabs, Badge
} from '@mantine/core';

import { DatePickerInput, MonthPickerInput } from '@mantine/dates';
import { IconAlertCircle, IconArrowLeft, IconCurrencyDollar, IconChevronUp, IconChevronDown, IconSelector, IconDownload } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import config from '../../../config';
import { notifications } from '@mantine/notifications';

const PRICE_AGE_LIMIT = { A: 30, B: 90 };
const PRICE_AGE_TIP = `Price freshness limit: Class A ≤${PRICE_AGE_LIMIT.A} days, Class B ≤${PRICE_AGE_LIMIT.B} days`;
function priceAgeStale(days, cls) {
  if (days == null || cls == null) return false;
  const limit = PRICE_AGE_LIMIT[cls.toUpperCase()];
  return limit != null && days > limit;
}

function getDosStrategy(dos, ssp) {
  if (dos == null || ssp == null || ssp === 0) return null;
  if (dos <= 60)  return { bracket: '0–60 days',    label: 'Hold to SSP',           pct: 0,    floor: ssp };
  if (dos <= 120) return { bracket: '61–120 days',  label: 'Patience',               pct: 5,    floor: Math.round(ssp * 0.95 * 100) / 100 };
  if (dos <= 180) return { bracket: '121–180 days', label: 'Deeper Look',            pct: 10,   floor: Math.round(ssp * 0.90 * 100) / 100 };
  if (dos <= 270) return { bracket: '181–270 days', label: 'Last Chance',            pct: 12,   floor: Math.round(ssp * 0.88 * 100) / 100 };
  return               { bracket: '271+ days',    label: 'Manager Guidance',       pct: 15,   floor: Math.round(ssp * 0.85 * 100) / 100 };
}

// Price-expiry limits for the effective basis (must match Scorecards.vSalesLineEffectivePricing:
// Class A > 90d, else > 180d). Distinct from PRICE_AGE_LIMIT above, which is only the "getting
// stale" red highlight — this one decides whether the DOS floor may be applied at all.
const EXPIRY_LIMIT = { A: 90, B: 180 };
function sspExpiredAtSale(priceAge, cls) {
  if (priceAge == null) return true; // can't verify freshness → treat as expired
  const limit = EXPIRY_LIMIT[(cls ?? '').toUpperCase()] ?? 180;
  return priceAge > limit;
}

// Explains, per row, which basis the effective price used and the math behind it —
// mirrors the query's precedence so a reviewer can see exactly what was done.
function basisInfo(row) {
  const cls = (row.class ?? '').toUpperCase();
  if (row.minSellPrice != null && row.minSellPrice !== 0)
    return { tag: 'override', detail: `Manual override: $${Number(row.minSellPrice).toLocaleString()}` };
  if (row.llp != null && row.llp !== 0)
    return { tag: 'LLP', detail: `Life-limited price: $${Number(row.llp).toLocaleString()}` };
  if (row.ssp == null || row.ssp <= 0)
    return { tag: 'no SSP', detail: 'No at-sale SSP — measured on the raw sell-price metric' };
  if (sspExpiredAtSale(row.priceAge, cls))
    return { tag: 'raw · expired', detail: `SSP expired at sale (price age ${row.priceAge}d > ${EXPIRY_LIMIT[cls] ?? 180}d limit) → no DOS discount; basis = at-sale SSP $${Number(row.ssp).toLocaleString()}` };
  const strat = getDosStrategy(row.dos, row.ssp);
  if (strat && strat.pct > 0 && row.effectiveSsp != null && row.effectiveSsp < row.ssp)
    return { tag: `DOS −${strat.pct}%`, detail: `${row.dos}d on shelf (${strat.bracket}, ${strat.label}): $${Number(row.ssp).toLocaleString()} × ${100 - strat.pct}% = $${Math.round(row.effectiveSsp).toLocaleString()}` };
  return { tag: 'hold', detail: `${row.dos ?? '?'}d on shelf (0–60d): no discount; basis = at-sale SSP $${Number(row.ssp).toLocaleString()}` };
}

// The default effective basis for a line, mirroring the view's precedence:
// manual override > LLP > DOS-guided floor (if SSP not expired at sale) > raw at-sale SSP.
// Used only for the optimistic UI update; the authoritative value comes from the server/view.
function effectiveBasisFor(row, minSellPrice) {
  if (minSellPrice != null && minSellPrice !== 0) return minSellPrice;
  if (row.llp != null && row.llp !== 0) return row.llp;
  if (row.ssp != null && row.ssp > 0 && !sspExpiredAtSale(row.priceAge, row.class)) {
    const strat = getDosStrategy(row.dos, row.ssp);
    if (strat && strat.floor != null) return strat.floor;
  }
  return row.ssp;
}

function MinSellPriceInput({ initialValue, onCommit, disabled }) {
  const [val, setVal] = useState(initialValue != null ? String(initialValue) : '');
  useEffect(() => { setVal(initialValue != null ? String(initialValue) : ''); }, [initialValue]);
  return (
    <TextInput
      type="number"
      size="xs"
      style={{ width: 72 }}
      value={val}
      disabled={disabled}
      onChange={e => setVal(e.currentTarget.value)}
      onBlur={() => {
        const parsed = val.trim() === '' ? null : parseFloat(val);
        if (parsed !== (initialValue ?? null)) onCommit(parsed);
      }}
    />
  );
}

function buildUrl(ccode, from, to) {
  const params = new URLSearchParams();
  if (ccode) params.set('ccode', ccode);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return `${config.apiBaseUrl}/sales/invoice-overrides/invoices?${params}`;
}

function buildUnpricedUrl(ccode, from, to) {
  const params = new URLSearchParams();
  if (ccode) params.set('ccode', ccode);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return `${config.apiBaseUrl}/sales/invoice-overrides/unpriced-invoices?${params}`;
}

function getPreviousQuarters(count = 8) {
  const today = new Date();
  let year = today.getFullYear();
  let quarter = Math.floor(today.getMonth() / 3) + 1;
  const quarters = [];
  for (let i = 0; i < count; i++) {
    quarters.push({ year, quarter });
    quarter--;
    if (quarter === 0) { quarter = 4; year--; }
  }
  return quarters;
}

function getQuarterDates(year, quarter) {
  const startMonth = (quarter - 1) * 3;
  return {
    from: new Date(year, startMonth, 1),
    to: new Date(year, startMonth + 3, 0),
  };
}

function toISO(date) {
  return date ? date.toISOString().split('T')[0] : null;
}

export default function AllInvoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const paramCcode = searchParams.get('ccode') ?? '';
  const paramFrom = searchParams.get('from') ?? '';
  const paramTo = searchParams.get('to') ?? '';
  const paramMode = searchParams.get('mode');
  const paramQuarter = searchParams.get('quarter');
  const paramMonth = searchParams.get('month');
  const paramPn = searchParams.get('pn') ?? '';
  const paramTab = searchParams.get('tab') ?? 'priced';

  const [ccode, setCcode] = useState(paramCcode);
  const [isUniversalMode] = useState(() => !paramCcode);
  const [tab, setTab] = useState(paramTab);

  const scorecardToday = useMemo(() => new Date(), []);
  const scorecardQStart = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
  }, []);
  const { data: scorecardData } = useAuthenticatedData(
    isUniversalMode
      ? `${config.apiBaseUrl}/SalesManager/Scorecard?from=${scorecardQStart.toISOString().split('T')[0]}&to=${scorecardToday.toISOString().split('T')[0]}`
      : null
  );
  const salespersonOptions = useMemo(() => {
    const rows = (scorecardData?.rows ?? [])
      .filter(r => !r.isAverageJoe && r.ccode !== 'Other')
      .map(r => ({ value: r.ccode, label: r.name ?? r.ccode }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: '', label: 'All salespeople' }, ...rows];
  }, [scorecardData]);

  const quarterOptions = useMemo(
    () => getPreviousQuarters(8).map(({ year, quarter }) => ({
      value: `${year}-${quarter}`,
      label: `Q${quarter} ${year}`,
    })),
    []
  );

  const [mode, setMode] = useState(paramMode ?? (paramFrom ? 'custom' : 'quarter'));

  const today = new Date();
  const currentQ = Math.floor(today.getMonth() / 3) + 1;
  const [selectedQuarter, setSelectedQuarter] = useState(
    paramQuarter ?? `${today.getFullYear()}-${currentQ}`
  );
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (paramMonth) {
      const [y, m] = paramMonth.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [customFrom, setCustomFrom] = useState(paramFrom ? new Date(paramFrom) : null);
  const [customTo, setCustomTo] = useState(paramTo ? new Date(paramTo) : null);
  const [pnFilter, setPnFilter] = useState(paramPn);

  useEffect(() => {
    const params = new URLSearchParams();
    if (ccode) params.set('ccode', ccode);
    params.set('mode', mode);
    if (mode === 'quarter' && selectedQuarter) params.set('quarter', selectedQuarter);
    if (mode === 'month' && selectedMonth) {
      const y = selectedMonth.getFullYear();
      const mo = String(selectedMonth.getMonth() + 1).padStart(2, '0');
      params.set('month', `${y}-${mo}`);
    }
    if (mode === 'custom') {
      if (customFrom) params.set('from', toISO(customFrom));
      if (customTo) params.set('to', toISO(customTo));
    }
    if (pnFilter.trim()) params.set('pn', pnFilter.trim());
    params.set('tab', tab);
    setSearchParams(params, { replace: true });
  }, [ccode, mode, selectedQuarter, selectedMonth, customFrom, customTo, pnFilter, tab, setSearchParams]);

  const { fromDate, toDate } = useMemo(() => {
    if (mode === 'quarter' && selectedQuarter) {
      const [y, q] = selectedQuarter.split('-').map(Number);
      const { from, to } = getQuarterDates(y, q);
      return { fromDate: toISO(from), toDate: toISO(to) };
    }
    if (mode === 'month' && selectedMonth) {
      const y = selectedMonth.getFullYear();
      const m = selectedMonth.getMonth();
      return {
        fromDate: toISO(new Date(y, m, 1)),
        toDate: toISO(new Date(y, m + 1, 0)),
      };
    }
    if (mode === 'custom' && customFrom && customTo) {
      return { fromDate: toISO(customFrom), toDate: toISO(customTo) };
    }
    return { fromDate: null, toDate: null };
  }, [mode, selectedQuarter, selectedMonth, customFrom, customTo]);

  const { data: rawData, isLoading, error } = useAuthenticatedData(
    fromDate && toDate ? buildUrl(ccode, fromDate, toDate) : null
  );

  const { data: rawUnpricedData, isLoading: unpricedLoading, error: unpricedError } = useAuthenticatedData(
    fromDate && toDate ? buildUnpricedUrl(ccode, fromDate, toDate) : null
  );

  const [localData, setLocalData] = useState(null);
  const rows = localData ?? rawData ?? [];

  const [localUnpricedData, setLocalUnpricedData] = useState(null);
  const unpricedRows = localUnpricedData ?? rawUnpricedData ?? [];

  const { post } = useAuthenticatedPost();

  const handleOverrideChange = useCallback(async (docNo, line, salespersonCcode, minSellPrice) => {
    if (docNo == null || line == null) return;
    const prev = [...(localData ?? rawData ?? [])];
    setLocalData(prev.map(r => {
      if (r.docNo !== docNo || r.line !== line) return r;
      // When a manual override is cleared, the basis falls back to the computed
      // default (DOS floor if the SSP wasn't expired at sale), not the raw SSP.
      const effSsp = effectiveBasisFor(r, minSellPrice);
      const effDollar = (r.unitPrice != null && effSsp != null)
        ? (r.unitPrice - effSsp) * (r.qty ?? 1)
        : r.amountDifference;
      const effPct = (r.unitPrice != null && effSsp != null && effSsp !== 0)
        ? ((r.unitPrice - effSsp) / effSsp) * 100
        : r.percentDifference;
      return { ...r, minSellPrice, isOverridden: minSellPrice != null, overrideSource: minSellPrice != null ? 'manual' : null, effectiveSsp: effSsp, amountDifference: effDollar, effectivePercentDifference: effPct };
    }));
    try {
      await post(`${config.apiBaseUrl}/sales/invoice-overrides`, {
        docNo, line, salespersonCcode, minSellPrice
      });
      if (minSellPrice == null) {
        notifications.show({ message: 'Min sell price override removed.', color: 'gray', position: 'bottom-right', autoClose: 3000 });
      } else {
        notifications.show({ message: `Min sell price set to $${minSellPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`, color: 'green', position: 'bottom-right', autoClose: 3000 });
      }
    } catch {
      setLocalData(prev);
      notifications.show({ message: 'Failed to save override. Please try again.', color: 'red', position: 'bottom-right', autoClose: 5000 });
    }
  }, [localData, rawData, post]);

  React.useEffect(() => {
    setLocalData(null);
  }, [rawData]);

  const handleUnpricedOverrideChange = useCallback(async (docNo, line, salespersonCcode, minSellPrice) => {
    if (docNo == null || line == null) return;
    const prev = [...(localUnpricedData ?? rawUnpricedData ?? [])];
    setLocalUnpricedData(prev.map(r => {
      if (r.docNo !== docNo || r.line !== line) return r;
      const effSsp = (minSellPrice != null && minSellPrice !== 0)
        ? minSellPrice
        : ((r.llp != null && r.llp !== 0) ? r.llp : null);
      const effPct = (r.unitPrice != null && effSsp != null && effSsp !== 0)
        ? ((r.unitPrice - effSsp) / effSsp) * 100
        : null;
      return { ...r, minSellPrice, isOverridden: minSellPrice != null, effectiveSsp: effSsp, effectivePercentDifference: effPct };
    }));
    try {
      await post(`${config.apiBaseUrl}/sales/invoice-overrides`, {
        docNo, line, salespersonCcode, minSellPrice
      });
      if (minSellPrice == null) {
        notifications.show({ message: 'Min sell price override removed.', color: 'gray', position: 'bottom-right', autoClose: 3000 });
      } else {
        notifications.show({ message: `Min sell price set to $${minSellPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`, color: 'green', position: 'bottom-right', autoClose: 3000 });
      }
    } catch {
      setLocalUnpricedData(prev);
      notifications.show({ message: 'Failed to save override. Please try again.', color: 'red', position: 'bottom-right', autoClose: 5000 });
    }
  }, [localUnpricedData, rawUnpricedData, post]);

  React.useEffect(() => {
    setLocalUnpricedData(null);
  }, [rawUnpricedData]);

  const isSalespersonView = !!paramCcode;
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const salespersonLabel = (Array.isArray(rows) && rows[0]?.salesperson) || paramCcode;

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = useCallback(field => {
    setSortField(prev => {
      if (prev === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else setSortDir('asc');
      return field;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortField || !Array.isArray(rows)) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortField, sortDir]);

  const Th = useCallback(({ field, children, style }) => {
    const active = sortField === field;
    const Icon = active ? (sortDir === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;
    return (
      <Table.Th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        onClick={() => handleSort(field)}>
        <Group gap={4} justify={style?.textAlign === 'right' ? 'flex-end' : 'flex-start'} wrap="nowrap">
          {style?.textAlign === 'right' && <Icon size={12} opacity={active ? 1 : 0.35} />}
          {children}
          {style?.textAlign !== 'right' && <Icon size={12} opacity={active ? 1 : 0.35} />}
        </Group>
      </Table.Th>
    );
  }, [sortField, sortDir, handleSort]);

  const filteredRows = useMemo(() => {
    const needle = pnFilter.trim().toUpperCase();
    if (!needle) return sortedRows;
    return (Array.isArray(sortedRows) ? sortedRows : []).filter(r =>
      (r.partnumber ?? '').toUpperCase().includes(needle)
    );
  }, [sortedRows, pnFilter]);

  const avgPct = useMemo(() => {
    const vals = (Array.isArray(filteredRows) ? filteredRows : [])
      .map(r => r.effectivePercentDifference ?? r.percentDifference)
      .filter(v => v != null);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [filteredRows]);

  const downloadCsv = useCallback(() => {
    const headers = ['Date','PN','Serial','Class','Cond','Desc','Account','Salesperson','Tag','Tag Date','Age','Received','DOS','Inv Price','SSP','Min Sell','Adj Min Sell','Eff SSP','$ SvSP','% SvSP'];
    const esc = v => {
      if (v == null) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rowsArr = Array.isArray(filteredRows) ? filteredRows : [];
    const lines = [
      headers.join(','),
      ...rowsArr.map(r => [
        r.date ? new Date(r.date).toLocaleDateString() : '',
        r.partnumber ?? '',
        r.logNo ?? '',
        r.class ?? '',
        r.condition ?? '',
        r.description ?? '',
        r.accountName ?? '',
        r.tag ?? '',
        r.latestTagDate ? new Date(r.latestTagDate).toLocaleDateString() : '',
        r.tagAgeSale ?? '',
        r.receiverDate ? new Date(r.receiverDate).toLocaleDateString() : '',
        r.dos ?? '',
        r.unitPrice ?? '',
        r.ssp ?? '',
        r.llp ?? '',
        r.minSellPrice != null ? String(r.minSellPrice) : '',
        r.effectiveSsp ?? '',
        r.amountDifference ?? '',
        (r.effectivePercentDifference ?? r.percentDifference) != null
          ? (r.effectivePercentDifference ?? r.percentDifference).toFixed(1)
          : '',
      ].map(esc).join(','))
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adjustments_${fromDate ?? ''}_${toDate ?? ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows, fromDate, toDate]);

  return (
    <Box p="xl">
      <Group mb="lg" justify="space-between">
        <Group>
          {!isUniversalMode && isSalespersonView && (
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/sales/manager/commissions')}>
              Back
            </Button>
          )}
          <Title order={2}>{!isUniversalMode && isSalespersonView ? `Invoices — ${salespersonLabel}` : 'All Invoices'}</Title>
        </Group>
        <Button
          variant="subtle"
          size="sm"
          leftSection={<IconDownload size={16} />}
          disabled={!Array.isArray(filteredRows) || filteredRows.length === 0}
          onClick={downloadCsv}
        >
          Download CSV
        </Button>
      </Group>

      <Paper withBorder p="md" mb="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="xl" align="flex-end" style={{ flex: 1 }} wrap="nowrap">
          <Radio.Group value={mode} onChange={setMode}>
            <Group gap="lg">
              <Radio value="quarter" label="Quarter" size="sm" />
              <Radio value="month" label="Month" size="sm" />
              <Radio value="custom" label="Custom" size="sm" />
            </Group>
          </Radio.Group>

          <Group gap="md" wrap="wrap" align="flex-end">
            {isUniversalMode && (
              <Select
                label="Salesperson"
                data={salespersonOptions}
                value={ccode || null}
                onChange={v => setCcode(v ?? '')}
                searchable
                clearable
                placeholder="All salespeople"
                style={{ width: 200 }}
                size="sm"
              />
            )}
            {mode === 'quarter' && (
              <Select
                label="Quarter"
                value={selectedQuarter}
                onChange={setSelectedQuarter}
                data={quarterOptions}
                style={{ width: 160 }}
                size="sm"
              />
            )}
            {mode === 'month' && (
              <MonthPickerInput
                label="Month"
                value={selectedMonth}
                onChange={setSelectedMonth}
                style={{ width: 180 }}
                size="sm"
              />
            )}
            {mode === 'custom' && (
              <>
                <DatePickerInput
                  label="From"
                  value={customFrom}
                  onChange={setCustomFrom}
                  style={{ width: 160 }}
                  size="sm"
                  clearable
                />
                <DatePickerInput
                  label="To"
                  value={customTo}
                  onChange={setCustomTo}
                  style={{ width: 160 }}
                  size="sm"
                  clearable
                />
              </>
            )}
          </Group>
        </Group>
        <TextInput
          label="Part Number"
          placeholder="find…"
          value={pnFilter}
          onChange={e => setPnFilter(e.currentTarget.value)}
          style={{ width: 150 }}
          size="sm"
        />
        {isSalespersonView && avgPct != null && (
          <Box style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <Text size="xs" c="dimmed">Avg % SvSP (period)</Text>
            <Text size="xl" fw={700} c={avgPct >= 0 ? 'green' : 'red'}>{avgPct.toFixed(1)}%</Text>
          </Box>
        )}
        </Group>
      </Paper>

      <Tabs value={tab} onChange={setTab}>
        <Group justify="space-between" align="center" mb="sm">
          <Tabs.List>
            <Tabs.Tab value="priced">Priced</Tabs.Tab>
            <Tabs.Tab value="unpriced">
              <Group gap={6} wrap="nowrap">
                Unpriced
                {Array.isArray(unpricedRows) && unpricedRows.length > 0 && (
                  <Badge size="xs" color="orange" variant="filled" circle>{unpricedRows.length}</Badge>
                )}
              </Group>
            </Tabs.Tab>
          </Tabs.List>
          <Text size="xs" c="dimmed">Class A &amp; B only</Text>
        </Group>

        <Tabs.Panel value="priced">
          {isLoading && <Loader />}
          {error && (
            <Alert icon={<IconAlertCircle />} color="red" mb="md">
              Failed to load invoices.
            </Alert>
          )}
          {!isLoading && !error && (
            <Paper withBorder>
              <ScrollArea h="calc(100vh - 320px)" offsetScrollbars>
                <Table highlightOnHover verticalSpacing={4} horizontalSpacing="xs" style={{ fontSize: 12 }} stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Th field="date">Date</Th>
                      <Th field="partnumber">PN</Th>
                      <Th field="logNo">Serial</Th>
                      <Th field="class">Class</Th>
                      <Th field="condition">Cond</Th>
                      <Th field="description">Desc</Th>
                      <Th field="accountName">Account</Th>
                      <Table.Th style={{ width: 40 }}>Key</Table.Th>
                      <Th field="salesperson">Salesperson</Th>
                      <Th field="tag" style={{ maxWidth: 90 }}>Tag</Th>
                      <Th field="latestTagDate" style={{ width: 82 }}>Tag Date</Th>
                      <Th field="tagAgeSale" style={{ textAlign: 'right', width: 52 }}>Age</Th>
                      <Th field="receiverDate" style={{ width: 82 }}>Received</Th>
                      <Th field="dos" style={{ textAlign: 'right', width: 44 }}>DOS</Th>
                      <Th field="priceAge" style={{ textAlign: 'right', width: 52 }}>Price Age</Th>
                      <Th field="unitPrice" style={{ textAlign: 'right', width: 72 }}>Inv Price</Th>
                      <Th field="ssp" style={{ textAlign: 'right', width: 72 }}>SSP</Th>
                      <Th field="llp" style={{ textAlign: 'right', width: 72 }}>Min Sell</Th>
                      <Table.Th style={{ width: 136 }}>Adj Min Sell</Table.Th>
                      <Th field="effectiveSsp" style={{ textAlign: 'right', width: 72 }}>Eff SSP</Th>
                      <Th field="amountDifference" style={{ textAlign: 'right', width: 72 }}>$ SvSP</Th>
                      <Th field="effectivePercentDifference" style={{ textAlign: 'right', width: 58 }}>% SvSP</Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(Array.isArray(filteredRows) ? filteredRows : []).map((row, i) => {
                      const rowBg = row.isOverridden
                        ? (isDark ? '#3d2e00' : '#fff9e6')
                        : undefined;
                      return (
                        <Table.Tr
                          key={`${row.docNo}-${row.line}-${i}`}
                          style={{ backgroundColor: rowBg, cursor: 'pointer' }}
                          onClick={e => {
                            if (e.target.closest('input') || e.target.closest('button')) return;
                            if (row.docNo && row.line != null)
                              navigate(`/sales/manager/invoices/${encodeURIComponent(row.docNo)}/${row.line}`);
                          }}
                        >
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.date ? new Date(row.date).toLocaleDateString() : '—'}</Table.Td>
                          <Table.Td style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.partnumber}>{row.partnumber}</Table.Td>
                          <Table.Td>{row.logNo ?? ''}</Table.Td>
                          <Table.Td>{row.class ?? ''}</Table.Td>
                          <Table.Td>{row.condition ?? ''}</Table.Td>
                          <Table.Td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description}>{row.description}</Table.Td>
                          <Table.Td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.accountName}>{row.accountName}</Table.Td>
                          <Table.Td>{row.isKeyAccount ? <Badge size="xs" color="teal" variant="filled">Y</Badge> : null}</Table.Td>
                          <Table.Td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.salesperson ?? ''}>{row.salesperson ?? ''}</Table.Td>
                          <Table.Td style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.tag ?? ''}>{row.tag ?? ''}</Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.latestTagDate ? new Date(row.latestTagDate).toLocaleDateString() : ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{row.tagAgeSale != null ? row.tagAgeSale.toLocaleString() : ''}</Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.receiverDate ? new Date(row.receiverDate).toLocaleDateString() : ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{row.dos != null ? row.dos.toLocaleString() : ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Tooltip label={PRICE_AGE_TIP} withArrow position="top">
                              <Text size="sm" c={priceAgeStale(row.priceAge, row.class) ? 'red' : undefined} fw={priceAgeStale(row.priceAge, row.class) ? 600 : undefined}>
                                {row.priceAge != null ? row.priceAge.toLocaleString() : '—'}
                              </Text>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.unitPrice != null ? `$${row.unitPrice.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.ssp != null ? `$${row.ssp.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {(row.llp != null && row.llp !== 0) ? `$${row.llp.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td onClick={e => e.stopPropagation()}>
                            <Group gap={4} wrap="nowrap">
                              <MinSellPriceInput
                                initialValue={row.minSellPrice}
                                disabled={row.docNo == null || row.line == null}
                                onCommit={parsed => handleOverrideChange(row.docNo, row.line, row.salespersonCcode ?? ccode, parsed)}
                              />
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                title="Set min sell price to invoice price"
                                disabled={row.docNo == null || row.line == null || row.unitPrice == null}
                                onClick={() => handleOverrideChange(row.docNo, row.line, row.salespersonCcode ?? ccode, row.unitPrice)}
                              >
                                <IconCurrencyDollar size={14} />
                              </ActionIcon>
                            </Group>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.effectiveSsp != null ? `$${row.effectiveSsp.toLocaleString()}` : '—'}
                            {(() => { const b = basisInfo(row); return (
                              <Tooltip label={b.detail} withArrow position="top" multiline w={280}>
                                <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>{b.tag}</Text>
                              </Tooltip>
                            ); })()}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.amountDifference != null ? `$${row.amountDifference.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {(row.effectivePercentDifference ?? row.percentDifference) != null
                              ? `${(row.effectivePercentDifference ?? row.percentDifference).toFixed(1)}%`
                              : '—'}
                            {row.percentDifference != null && row.effectivePercentDifference != null
                              && Math.abs(row.effectivePercentDifference - row.percentDifference) > 0.05 && (
                              <Text size="xs" c="dimmed" fs="italic">pre {row.percentDifference.toFixed(1)}%</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                    {(!Array.isArray(filteredRows) || filteredRows.length === 0) && (
                      <Table.Tr>
                        <Table.Td colSpan={22}>
                          <Text c="dimmed" ta="center" py="md">No invoices found for the selected range.</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="unpriced">
          {unpricedLoading && <Loader />}
          {unpricedError && (
            <Alert icon={<IconAlertCircle />} color="red" mb="md">
              Failed to load unpriced invoices.
            </Alert>
          )}
          {!unpricedLoading && !unpricedError && (
            <Paper withBorder>
              <ScrollArea h="calc(100vh - 320px)" offsetScrollbars>
                <Table highlightOnHover verticalSpacing={4} horizontalSpacing="xs" style={{ fontSize: 12 }} stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>PN</Table.Th>
                      <Table.Th>Cond</Table.Th>
                      <Table.Th>Class</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Account</Table.Th>
                      <Table.Th style={{ width: 40 }}>Key</Table.Th>
                      <Table.Th>Salesperson</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 40 }}>Qty</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 80 }}>Inv Price</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 44 }}>DOS</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 52 }}>Price Age</Table.Th>
                      <Table.Th style={{ maxWidth: 90 }}>Tag</Table.Th>
                      <Table.Th style={{ width: 82 }}>Tag Date</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 52 }}>Age</Table.Th>
                      <Table.Th style={{ width: 82 }}>Received</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 72 }}>Min Sell</Table.Th>
                      <Table.Th style={{ width: 88 }}>Adj Min Sell</Table.Th>
                      <Table.Th style={{ textAlign: 'right', width: 72 }}>Eff SvSP%</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(Array.isArray(unpricedRows) ? unpricedRows : []).map((row, i) => {
                      const rowBg = row.isOverridden
                        ? (isDark ? '#0c2d2a' : '#f0faf9')
                        : undefined;
                      return (
                        <Table.Tr
                          key={`unpriced-${row.docNo}-${row.line}-${i}`}
                          style={{ backgroundColor: rowBg, cursor: 'pointer' }}
                          onClick={e => {
                            if (e.target.closest('input') || e.target.closest('button')) return;
                            if (row.docNo && row.line != null)
                              navigate(`/sales/manager/invoices/${encodeURIComponent(row.docNo)}/${row.line}`);
                          }}
                        >
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.date ? new Date(row.date).toLocaleDateString() : '—'}</Table.Td>
                          <Table.Td style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.partnumber}>{row.partnumber}</Table.Td>
                          <Table.Td>{row.condition ?? ''}</Table.Td>
                          <Table.Td>{row.class ?? ''}</Table.Td>
                          <Table.Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description}>{row.description}</Table.Td>
                          <Table.Td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.accountName}>{row.accountName}</Table.Td>
                          <Table.Td>{row.isKeyAccount ? <Badge size="xs" color="teal" variant="filled">Y</Badge> : null}</Table.Td>
                          <Table.Td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.salesperson ?? ''}>{row.salesperson ?? ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{row.qty ?? ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.unitPrice != null ? `$${row.unitPrice.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{row.dos != null ? row.dos.toLocaleString() : '—'}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Tooltip label={PRICE_AGE_TIP} withArrow position="top">
                              <Text size="sm" c={priceAgeStale(row.priceAge, row.class) ? 'red' : undefined} fw={priceAgeStale(row.priceAge, row.class) ? 600 : undefined}>
                                {row.priceAge != null ? row.priceAge.toLocaleString() : '—'}
                              </Text>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.tag ?? ''}>{row.tag ?? ''}</Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.latestTagDate ? new Date(row.latestTagDate).toLocaleDateString() : ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{row.tagAgeSale != null ? row.tagAgeSale.toLocaleString() : ''}</Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>{row.receiverDate ? new Date(row.receiverDate).toLocaleDateString() : ''}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {(row.llp != null && row.llp !== 0) ? `$${row.llp.toLocaleString()}` : '—'}
                          </Table.Td>
                          <Table.Td onClick={e => e.stopPropagation()}>
                            <Group gap={4} wrap="nowrap">
                              <MinSellPriceInput
                                initialValue={row.minSellPrice}
                                disabled={row.docNo == null || row.line == null}
                                onCommit={parsed => handleUnpricedOverrideChange(row.docNo, row.line, row.salespersonCcode ?? ccode, parsed)}
                              />
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                title="Set min sell price to invoice price"
                                disabled={row.docNo == null || row.line == null || row.unitPrice == null}
                                onClick={() => handleUnpricedOverrideChange(row.docNo, row.line, row.salespersonCcode ?? ccode, row.unitPrice)}
                              >
                                <IconCurrencyDollar size={14} />
                              </ActionIcon>
                            </Group>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {row.effectivePercentDifference != null
                              ? <Text size="sm" c={row.effectivePercentDifference >= 0 ? 'green' : 'orange'} fw={500}>
                                  {row.effectivePercentDifference.toFixed(1)}%
                                </Text>
                              : <Text size="sm" c="dimmed">—</Text>}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                    {(!Array.isArray(unpricedRows) || unpricedRows.length === 0) && (
                      <Table.Tr>
                        <Table.Td colSpan={19}>
                          <Text c="dimmed" ta="center" py="md">No unpriced invoices found for the selected range.</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          )}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
